import React from "react";
import { 
  BadgeCheck, 
  Star, 
  Truck, 
  Award,
  Shield
} from "lucide-react";

const badgeConfig = {
  "verified": {
    icon: BadgeCheck,
    label: "Verified Partner",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    iconColor: "text-blue-600"
  },
  "top rated": {
    icon: Star,
    label: "Top Rated",
    color: "bg-yellow-100 text-yellow-700 border-yellow-200",
    iconColor: "text-yellow-500"
  },
  "highly rated": {
    icon: Star,
    label: "Highly Rated",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    iconColor: "text-amber-500"
  },
  "100+ deliveries": {
    icon: Award,
    label: "100+ Deliveries",
    color: "bg-purple-100 text-purple-700 border-purple-200",
    iconColor: "text-purple-600"
  },
  "50+ deliveries": {
    icon: Award,
    label: "50+ Deliveries",
    color: "bg-indigo-100 text-indigo-700 border-indigo-200",
    iconColor: "text-indigo-600"
  },
  "10+ deliveries": {
    icon: Truck,
    label: "10+ Deliveries",
    color: "bg-gray-100 text-gray-700 border-gray-200",
    iconColor: "text-gray-600"
  },
  "trusted courier": {
    icon: Shield,
    label: "Trusted Courier",
    color: "bg-green-100 text-green-700 border-green-200",
    iconColor: "text-green-600"
  }
};

/**
 * Single badge component
 */
export function PartnerBadge({ badge, size = "sm" }) {
  const config = badgeConfig[badge];
  if (!config) return null;
  
  const Icon = config.icon;
  const sizeClasses = size === "sm" 
    ? "text-xs px-2 py-0.5 gap-1" 
    : "text-sm px-3 py-1 gap-1.5";
  const iconSize = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  
  return (
    <span 
      className={`inline-flex items-center border rounded-full font-medium ${config.color} ${sizeClasses}`}
      data-testid={`badge-${badge.replace(/\s+/g, '-')}`}
    >
      <Icon className={`${iconSize} ${config.iconColor}`} />
      {config.label}
    </span>
  );
}

/**
 * Display multiple partner badges
 */
export function PartnerBadges({ badges = [], verified = false, size = "sm", maxShow = 3 }) {
  // Ensure verified badge comes first if partner is verified
  const allBadges = verified && !badges.includes("verified") 
    ? ["verified", ...badges] 
    : badges;
  
  const displayBadges = allBadges.slice(0, maxShow);
  const remaining = allBadges.length - maxShow;
  
  if (displayBadges.length === 0) return null;
  
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="partner-badges">
      {displayBadges.map((badge) => (
        <PartnerBadge key={badge} badge={badge} size={size} />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-muted-foreground">+{remaining} more</span>
      )}
    </div>
  );
}

/**
 * Verified checkmark icon (for inline use)
 */
export function VerifiedBadgeIcon({ className = "" }) {
  return (
    <BadgeCheck 
      className={`w-5 h-5 text-blue-600 ${className}`} 
      data-testid="verified-icon"
    />
  );
}
